import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  LinearProgress,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import SyncIcon from '@mui/icons-material/Sync'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import { purchasableStatusAdminService } from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'
import {
  apiDataUnavailableReason,
  canDownloadApiData,
} from './purchasableStatusApiDownload'

const POLL_INTERVAL_MS = 5000

const formatDate = (iso) => {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatCount = (value) => (Number(value) || 0).toLocaleString()

const formatDuration = (startedAt, finishedAt) => {
  if (!startedAt) return '—'
  const from = new Date(startedAt).getTime()
  const to = finishedAt ? new Date(finishedAt).getTime() : Date.now()
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return '—'
  const totalSec = Math.floor((to - from) / 1000)
  const hours = Math.floor(totalSec / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

const statusColor = (run) => {
  if (!run) return 'default'
  if (run.status === 'running') return 'info'
  if (run.status === 'failed') return 'error'
  if ((run.missing || 0) > 0 || (run.invalid || 0) > 0 || (run.write_failures || 0) > 0) {
    return 'warning'
  }
  return 'success'
}

const statusLabel = (run) => {
  if (!run) return '—'
  if (run.status === 'running' && run.possibly_stuck) return 'Possibly stuck'
  if (run.status === 'running') return 'Running'
  if (run.status === 'failed') return 'Failed'
  if (run.status === 'completed') return 'Completed'
  return run.status
}

const Stat = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {formatCount(value)}
    </Typography>
  </Box>
)

const PurchasableStatusAdminPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.STOCK_SYNC,
    'write',
  )
  const [latest, setLatest] = useState(null)
  const [starting, setStarting] = useState(false)
  const [success, setSuccess] = useState(null)
  const [history, setHistory] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(true)
  const [error, setError] = useState(null)
  const [nowTick, setNowTick] = useState(Date.now())
  const [downloadingKey, setDownloadingKey] = useState(null)
  const [unavailableIds, setUnavailableIds] = useState({})
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' })

  const loadLatest = useCallback(async () => {
    const data = await purchasableStatusAdminService.getLatestRun()
    setLatest(data || null)
  }, [])

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const data = await purchasableStatusAdminService.listRuns({
        page: page + 1,
        limit: rowsPerPage,
      })
      setHistory(data?.items || [])
      setTotal(data?.pagination?.total || 0)
    } finally {
      setHistoryLoading(false)
    }
  }, [page, rowsPerPage])

  const refresh = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      await Promise.all([loadLatest(), loadHistory()])
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to load purchasable status runs'
      setError(typeof message === 'string' ? message : 'Failed to load purchasable status runs')
    } finally {
      setLoading(false)
    }
  }, [loadLatest, loadHistory])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const latestRunning = latest?.status === 'running'

  useEffect(() => {
    if (!latestRunning) return undefined
    const timer = setInterval(() => {
      setNowTick(Date.now())
      void loadLatest().catch(() => {})
      void loadHistory().catch(() => {})
    }, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [latestRunning, loadLatest, loadHistory])

  const handleRun = async () => {
    try {
      setStarting(true)
      setError(null)
      setSuccess(null)
      const data = await purchasableStatusAdminService.run()
      setSuccess(data?.message || 'Purchasable status sync started')
      await refresh()
    } catch (err) {
      const status = err?.response?.status
      const message = err?.response?.data?.message || err?.response?.data?.error
      setError(
        status === 409
          ? message || 'Purchasable status sync is already running'
          : message || 'Failed to start purchasable status sync',
      )
    } finally {
      setStarting(false)
    }
  }

  const handleDownloadApiData = async (runId) => {
    const unavailableKey = `${runId}:api-data`
    try {
      setDownloadingKey(unavailableKey)
      const data = await purchasableStatusAdminService.getApiData(runId)
      if (!data?.download_url) {
        throw new Error('No download URL')
      }
      window.open(data.download_url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      const status = err.response?.status
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to download API data'
      if (status === 404) {
        setUnavailableIds((prev) => ({ ...prev, [unavailableKey]: true }))
        setSnackbar({
          open: true,
          severity: 'warning',
          message: typeof message === 'string' ? message : 'Artifact is no longer available',
        })
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: typeof message === 'string' ? message : 'Failed to download API data',
        })
      }
    } finally {
      setDownloadingKey(null)
    }
  }

  const seen = Number(latest?.products_seen) || 0
  const totalProducts = Number(latest?.products_total) || 0
  const progressPct =
    totalProducts > 0
      ? Math.min(100, Math.round((seen / totalProducts) * 100))
      : latestRunning
        ? 0
        : latest
          ? 100
          : 0

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <ToggleOnIcon color="primary" fontSize="large" />
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Purchasable status sync
        </Typography>
        <Button
          variant="outlined"
          startIcon={loading || historyLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={() => void refresh()}
          disabled={loading || historyLoading}
        >
          Refresh
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        The scheduled job checks Shwapno article codes in pages of 50 and updates whether each
        product can be purchased. This page follows that run. History is kept for 30 days.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Manual sync
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {canWrite
                  ? 'Starts the same job as the cron. A second start is skipped while one is already running.'
                  : 'You can view runs. Starting a sync requires write permission.'}
              </Typography>
            </Box>
            {canWrite && (
              <Button
                variant="contained"
                startIcon={starting ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                onClick={() => void handleRun()}
                disabled={starting || latestRunning || loading}
              >
                {starting ? 'Starting…' : latestRunning ? 'Sync running…' : 'Run purchasable status sync'}
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        {latestRunning && <LinearProgress />}
        <CardContent>
          {!latest && !loading && (
            <Typography color="text.secondary">No purchasable status run yet.</Typography>
          )}
          {latest && (
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip label={statusLabel(latest)} color={statusColor(latest)} size="small" />
                  <Typography variant="body2" color="text.secondary">
                    Started {formatDate(latest.started_at)} · Duration {formatDuration(latest.started_at, latest.finished_at)}
                  </Typography>
                </Stack>
                <Tooltip
                  title={
                    canDownloadApiData(latest, unavailableIds)
                      ? 'API Data CSV is kept for 7 days'
                      : apiDataUnavailableReason(latest)
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={
                        downloadingKey === `${latest.id}:api-data` ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      disabled={
                        !canDownloadApiData(latest, unavailableIds) ||
                        downloadingKey === `${latest.id}:api-data`
                      }
                      onClick={() => void handleDownloadApiData(latest.id)}
                    >
                      Download API Data
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCount(seen)} / {formatCount(totalProducts)} products
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progressPct}%
                  </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={progressPct} />
              </Box>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Stat label="Updated" value={latest.rows_updated} />
                <Stat label="Unchanged" value={latest.unchanged} />
                <Stat label="Missing" value={latest.missing} />
                <Stat label="Invalid" value={latest.invalid} />
                <Stat label="Write failures" value={latest.write_failures} />
                <Stat label="Pages" value={latest.pages} />
              </Stack>
              {latest.error_summary && (
                <Alert severity="error">{latest.error_summary}</Alert>
              )}
              {latest.possibly_stuck && latestRunning && (
                <Alert severity="warning">
                  No progress for several minutes. The job may have stopped without finishing.
                </Alert>
              )}
              {(latest.missing_sample || []).length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Sample missing article codes: {latest.missing_sample.slice(0, 20).join(', ')}
                  {latest.missing > latest.missing_sample.length
                    ? ` (+${latest.missing - latest.missing_sample.length} more)`
                    : ''}
                </Typography>
              )}
              {(latest.invalid_sample || []).length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Sample invalid statuses: {latest.invalid_sample.slice(0, 20).join(', ')}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Last progress {formatDate(latest.last_progress_at)}
                {latestRunning ? ` · refreshed ${new Date(nowTick).toLocaleTimeString()}` : ''}
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            History
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Started', 'Status', 'Trigger', 'Seen', 'Updated', 'Unchanged', 'Missing', 'Invalid', 'Duration', 'API data'].map((label) => (
                    <TableCell key={label} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((run) => (
                  <TableRow key={run.id} hover>
                    <TableCell>{formatDate(run.started_at)}</TableCell>
                    <TableCell>
                      <Chip label={statusLabel(run)} color={statusColor(run)} size="small" />
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {run.trigger || 'cron'}
                      {run.triggered_by_email ? ` · ${run.triggered_by_email}` : ''}
                    </TableCell>
                    <TableCell>{formatCount(run.products_seen)}</TableCell>
                    <TableCell>{formatCount(run.rows_updated)}</TableCell>
                    <TableCell>{formatCount(run.unchanged)}</TableCell>
                    <TableCell>{formatCount(run.missing)}</TableCell>
                    <TableCell>{formatCount(run.invalid)}</TableCell>
                    <TableCell>{formatDuration(run.started_at, run.finished_at)}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      {run.status === 'running' || (!run.api_data_csv_s3_key && !unavailableIds[`${run.id}:api-data`]) ? (
                        <Typography component="span" variant="body2" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        <Tooltip
                          title={
                            canDownloadApiData(run, unavailableIds)
                              ? 'API Data CSV is kept for 7 days'
                              : apiDataUnavailableReason(run)
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              aria-label={
                                canDownloadApiData(run, unavailableIds)
                                  ? 'Download API Data'
                                  : apiDataUnavailableReason(run)
                              }
                              disabled={
                                !canDownloadApiData(run, unavailableIds) ||
                                downloadingKey === `${run.id}:api-data`
                              }
                              onClick={() => void handleDownloadApiData(run.id)}
                            >
                              {downloadingKey === `${run.id}:api-data` ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!historyLoading && history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <Typography color="text.secondary">No runs in the last 30 days.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardContent>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default PurchasableStatusAdminPage
